import { css } from '@emotion/react';
import { type IconProp } from '@fortawesome/fontawesome-svg-core';
import { faEllipsisV } from '@fortawesome/pro-solid-svg-icons';
import React, {
  type FC, type Ref, useCallback, useState,
} from 'react';
import {
  RoundButtonComponent, RoundButtonSize,
} from '~/_shared/baseComponents/buttons';
import { RoundButtonStyle } from '~/_shared/baseComponents/buttons/roundButton/roundButton.styles';
import {
  ContextMenuComponent, ContextMenuDefaultItem, ContextMenuItemCoreComponent,
} from '~/_shared/baseComponents/contextMenu';
import {
  TooltipBehavior, TooltipComponent,
} from '~/_shared/baseComponents/tooltip/tooltip.component';
import { LocationListingItemAddressSkeletonComponent } from '~/_shared/components/locationListingPanel/item/locationListingItemAddressSkeleton.component';
import { LabelOrMarkerVisualizerContainer } from '~/_shared/labelOrMarkerVisualizer/labelOrMarkerVisualizer.container';
import { useTheme } from '~/_shared/themes/theme.hooks';
import type { Theme } from '~/_shared/themes/theme.model';
import {
  type FetchableResource, FetchableResourceStatus,
} from '~/_shared/types/fetchableResource/fetchableResource';
import { isResourceLoaded } from '~/_shared/types/fetchableResource/fetchableResource.helpers';
import { MarkerSettingType } from '~/_shared/types/markers/visualSettings.enums';
import { type MarkerSettings } from '~/_shared/types/markers/visualSettings.types';
import { INDICATOR_WIDTH } from '~/sidebar/sidebarApps/rightSidebar/sidebarTitle/sidebarTitle.component';

const TOOLTIP_DELAY = 500;
const ITEM_HEIGHT = 88;

const markerStyle = css({
  display: 'flex',
  flexShrink: 0,
  justifyContent: 'center',
  marginRight: 12,
  width: INDICATOR_WIDTH,
});

const getBGColor = (index: number, isHovered: boolean) => (theme: Theme) => {
  if (isHovered) {
    return theme.backgroundColors.secondaryHover;
  }

  return index % 2 === 0 ? theme.backgroundColors.primary : theme.backgroundColors.secondary;
};

const rootStyle = (index: number) => (theme: Theme) => css({
  position: 'relative',
  backgroundColor: getBGColor(index, false)(theme),
  boxSizing: 'border-box',
  color: theme.textColors.primary,
  maxHeight: ITEM_HEIGHT,

  width: '100%',
  ':hover': {
    backgroundColor: getBGColor(index, true)(theme),
  },
});

const infoPortionStyle = ({ isCentered }: { isCentered: boolean }) => css({
  display: 'flex',
  padding: '12px 56px 12px 12px',
  userSelect: 'none',
  ...(isCentered && { alignItems: 'center' }),
});

const linesStyle = css({
  boxSizing: 'border-box',
  maxWidth: `calc(100% - ${INDICATOR_WIDTH}px)`,
});

const lineTextStyle = css({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: '100%',
});

const headerStyle = css(lineTextStyle, {
  fontWeight: 500,
  fontSize: '20px',
});

const lineStyle = css(lineTextStyle, {
  fontWeight: 400,
  fontSize: '14px',
});

const settingsButtonWrapperStyle = css({
  position: 'absolute',
  right: 12,
  top: 12,
});

const linesLimit = 4;

export type LocationListingActions = Readonly<{
  icon?: IconProp;
  text: string;
  isDisabled: boolean;
  isDestructive: boolean;
  preventCloseOnClick?: boolean;
  onClick: () => void;
}>;

export type LocationListingItemProps = Readonly<{
  actions?: LocationListingActions[];
  lines?: FetchableResource<ReadonlyArray<string>>;
  header?: string;
  index: number;
  markerVisualSettings: MarkerSettings;
  readOnlyMode?: boolean;
  labelText?: string;

  onClick?: () => void;
  onHover?: () => void;
  onMouseLeave?: () => void;
}>;

export const LocationListingItemComponent: FC<LocationListingItemProps> = ({
  actions,
  lines,
  header,
  index,
  markerVisualSettings,
  readOnlyMode,
  labelText,
  onClick,
  onHover,
  onMouseLeave,
  ...restProps
}) => {
  const theme = useTheme();
  const hasMoreLinesThanCanFit = (lines?.value?.length ?? 0) > linesLimit;
  const [isSettingsVisible, setIsSettingsVisible] = useState<boolean>(false);
  const [isHovered, setIsHovered] = useState(false);

  const onMenuClick = useCallback(() => {
    setIsSettingsVisible(!isSettingsVisible);
  }, [isSettingsVisible]);

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
    onHover?.();
  }, [onHover]);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    onMouseLeave?.();
  }, [onMouseLeave]);

  return (
    <div
      {...restProps}
      css={rootStyle(index)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        css={infoPortionStyle({
          isCentered: lines?.status !== FetchableResourceStatus.Loading
            && !lines?.value?.length,
        })}
        onClick={onClick}
      >
        <div css={markerStyle}>
          <LabelOrMarkerVisualizerContainer
            labelText={labelText}
            maxDimensions={{ width: INDICATOR_WIDTH, height: INDICATOR_WIDTH }}
            maxSizeEasing
            markerSettings={markerVisualSettings}
            underlayColor={getBGColor(index, isHovered)(theme)}
            visualSettingsType={markerVisualSettings.useMarker ? MarkerSettingType.Marker : MarkerSettingType.Label}
          />
        </div>
        <div css={linesStyle}>
          {header && (
            <TooltipComponent
              tooltipContent={header}
              behavior={TooltipBehavior.ShowOnOverflow}
              delayShow={TOOLTIP_DELAY}
            >
              <div css={headerStyle}>{header}</div>
            </TooltipComponent>
          )}
          {isResourceLoaded(lines) &&
            lines.value
              .slice(0, hasMoreLinesThanCanFit ? linesLimit - 1 : undefined)
              .map((line, index) => (
                <div
                  key={index}
                  css={lineStyle}
                >
                  {line}
                </div>
              ))
          }
          {lines?.status === FetchableResourceStatus.Loading && (
            <LocationListingItemAddressSkeletonComponent
              animationColor={index % 2 === 1 ? undefined : theme.backgroundColors.secondaryHover}
              animationDelayMs={index * 200}
              animationDurationMs={2000}
              backgroundColor={index % 2 === 1 ? theme.backgroundColors.primary : theme.backgroundColors.secondary}
            />
          )}
          {hasMoreLinesThanCanFit && (
            <div
              css={lineStyle}
              title={lines?.value?.join('\n')}
            >
              ...
            </div>
          )}
        </div>
      </div>

      <ContextMenuComponent
        isFixed
        maxWidth={280}
        renderMenuContent={() => (
          <>
            {actions && actions.map((action, actionIndex) => (
              <ContextMenuItemCoreComponent
                key={actionIndex}
                closeOnClick={!action.preventCloseOnClick}
              >
                <ContextMenuDefaultItem
                  icon={action.icon}
                  onClick={action.onClick}
                  isDisabled={action.isDisabled}
                  isDestructive={action.isDestructive}
                >
                  {action.text}
                </ContextMenuDefaultItem>
              </ContextMenuItemCoreComponent>
            ))}
          </>
        )}
        isVisible={isSettingsVisible}
        onHide={() => setIsSettingsVisible(false)}
        preferredPlacement="bottom-end"
      >
        {ref => (readOnlyMode ? false : (
          <div css={settingsButtonWrapperStyle}>
            <RoundButtonComponent
              ref={ref as Ref<HTMLButtonElement>}
              buttonStyle={index % 2 === 0 ? RoundButtonStyle.Secondary : RoundButtonStyle.Primary}
              onClick={onMenuClick}
              icon={faEllipsisV}
              size={RoundButtonSize.Small}
              data-testid={`location-listing-item-menu-button-${header}`}
            />
          </div>
        )
        )}
      </ContextMenuComponent>
    </div>
  );
};
